import { useMemo, useState } from 'react';
import styled from 'styled-components';

import AIIcon from '@/components/Badge/AIBadge/AIIcon';
import { Flex, Space, VFlex } from '@/components/base';
import Typo from '@/components/Typography/Typo';
import TicketLabel from '@/features/Commons/Ticket/TicketLabel';

import FeedbackDetailBodyArea from '../../FeedbackBody/FeedbackDetailBodyArea';
import { AI_SUMMARY_NEW_LINE_SEPERATOR } from '../../FeedbackDetailModal/FeedbackDetailModal';
import FeedbackDetailPropertyArea from '../../PropertyArea/FeedbackDetailPropertyArea';
import feedbackDetailUtils from '../../utils/feedbackDetailutils';
import type { FeedbackStaticDetailTemplateT } from './types';

const FeedbackStaticDetailTemplate = (props: FeedbackStaticDetailTemplateT) => {
  const { detail, feedback } = props;

  const chatText: ChatMessageT[] = useMemo(() => {
    return feedbackDetailUtils.parseChatMessage(
      detail?.chat_text || [],
      feedback.source.type,
    );
  }, [detail?.chat_text, feedback?.source]);

  const chatTitle = useMemo(() => {
    return (
      chatText?.find((chat) => chat.author?.type === 3)?.author?.name || ''
    );
  }, [chatText]);

  const parsedAISummary: string[] = detail?.summary
    ? detail.summary.split(AI_SUMMARY_NEW_LINE_SEPERATOR)
    : [];

  const [showFull, setShowFull] = useState(false);

  return (
    <VFlex style={{ width: '100%' }}>
      <Flex g="24px" a="baseline">
        <TicketLabel type="feedback" style={{ marginTop: 10 }}>
          {feedback.tid}
        </TicketLabel>
        <Flex
          style={{ minHeight: 40, wordBreak: 'break-all', width: '80%' }}
          a="center"
        >
          <Typo className="text-gray-700" type="body1" weight={'sb'}>
            {feedback.title || 'Untitled'}
          </Typo>
        </Flex>
      </Flex>
      <Space h="16px" />
      {detail && (
        <FeedbackDetailPropertyArea
          static
          detailProps={{
            feedbackAlias: props.feedback.alias,
            detail,
          }}
          showFull={showFull}
          setShowFull={setShowFull}
        />
      )}

      {detail?.summary && (
        <>
          <Space h="12px" />
          <AISummaryBox>
            <Flex a="center" g="8px">
              <AIIcon />
              <Typo type="label3" className="text-gray-700" weight="b">
                AI summary
              </Typo>
            </Flex>
            <ul>
              {parsedAISummary.map((summary, index) => (
                <li key={index}>
                  <Typo type="body3">{summary}</Typo>
                </li>
              ))}
            </ul>
          </AISummaryBox>
        </>
      )}
      <Space h="24px" />
      <FeedbackDetailBodyArea
        feedbackAlias={props.feedback.alias}
        detail={detail}
        chatText={chatText}
        chatTitle={chatTitle}
        feedbackDetailFullView={false}
        options={{
          focusedBlock: props.focusedBlock,
        }}
      />
    </VFlex>
  );
};

export default FeedbackStaticDetailTemplate;

const AISummaryBox = styled(Flex)`
  flex-direction: column;
  background-color: ${(props) => props.theme.colors.gray200};
  padding: 16px;
  border-radius: 6px;
  ul {
    padding-left: 24px;
  }
`;
